import { Box, Button, CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { selectedRegistrationState } from '~/state/atoms/image/imageAtoms';
import { datasourceModalOpenState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import {
    downloadItemsToDisk,
    ExportItems,
} from '~/utils/io/files/downloadDicom';
import { transformPatientData } from '~/utils/transforms/patientDataTransforms';
import PatientAccordion from '../PatientAccordion';
import useActiveItems from '../useActiveItems';
import DatasetTree from './DatasetTree';

export type SelectedDatasets = {
    image?: Image3D;
    structureSet?: StructureSet;
    dose?: DoseMatrix;
    registration?: RigidRegistration;
    blendImage?: Image3D;
};

const createExportItems = (datasets: SelectedDatasets): ExportItems => ({
    images: [
        ...(datasets.image ? [datasets.image] : []),
        ...(datasets.blendImage ? [datasets.blendImage] : []),
    ],
    doses: datasets.dose ? [datasets.dose] : [],
    structureSets: datasets.structureSet ? [datasets.structureSet] : [],
    registrations: datasets.registration ? [datasets.registration] : [],
});

export default function LoadedPatientDataTree({
    patient,
}: {
    patient: Patient;
}) {
    const [exporting, setExporting] = useState(false);
    const [selectedDatasets, setSelectedDatasets] = useState<SelectedDatasets>(
        {}
    );
    const setDatasourceModal = useSetRecoilState(datasourceModalOpenState);
    const setSelectedRegistration = useSetRecoilState(
        selectedRegistrationState
    );
    const activeItems = useActiveItems();

    const { initializeEnvironment, setEnvironmentBlendImage } =
        useViewportEnvironment();

    const handleDownloadDatasets = async () => {
        try {
            setExporting(true);
            await downloadItemsToDisk(
                createExportItems(selectedDatasets),
                `${patient.dataSet.PatientName}_export`
            );
            setExporting(false);
        } catch (error) {
            handleError(error, 'Exporting files failed, please try again.');
        }
    };

    const handleLoadDatasets = () => {
        initializeEnvironment({
            images: selectedDatasets.image ? [selectedDatasets.image] : [],
            doses: selectedDatasets.dose ? [selectedDatasets.dose] : [],
            structureSets: selectedDatasets.structureSet
                ? [selectedDatasets.structureSet]
                : [],
            registrations: [],
            patients: [],
        });
        if (selectedDatasets?.blendImage && selectedDatasets.registration) {
            setEnvironmentBlendImage(
                selectedDatasets.blendImage,
                selectedDatasets.registration
            );
            setSelectedRegistration(selectedDatasets.registration);
        }
        setDatasourceModal(false);
    };

    const datasetSelected = Object.keys(selectedDatasets).length > 0;
    const data = transformPatientData(patient);

    return (
        <PatientAccordion patientKey={patient.id} patientData={data}>
            {data?.series ? (
                <Box>
                    <DatasetTree
                        patient={patient}
                        selectedDatasets={selectedDatasets}
                        activeItems={activeItems}
                        onSelect={setSelectedDatasets}
                    />
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={1}
                    >
                        <Button
                            onClick={handleDownloadDatasets}
                            variant="text"
                            color="primary"
                            size="small"
                            disabled={!datasetSelected || exporting}
                            startIcon={
                                exporting ? (
                                    <CircularProgress size={12} />
                                ) : (
                                    <Download size={12} />
                                )
                            }
                        >
                            <FormattedMessage id="downloadDatasets" />
                        </Button>
                        <Button
                            onClick={handleLoadDatasets}
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={!datasetSelected || exporting}
                        >
                            <FormattedMessage id="load" />
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Typography>No datasets for this patient.</Typography>
            )}
        </PatientAccordion>
    );
}
